import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useSession } from '@/components/auth/session';
import { Age, Empty, EventLog, MetaGrid, RepoChip, TaskKey } from '@/components/common/bits';
import { BriefIcon } from '@/components/common/StatusIcon';
import { ScheduleActions, SchedulePausedChip } from '@/features/schedules/ScheduleActions';
import { TemplatePlan } from '@/features/schedules/TemplatePlan';
import { useSchedule } from '@/hooks/use-fleet';
import type { ScheduleSummary } from '@/lib/api';
import { labelForKind } from '@/lib/event-kinds';
import { clockTime, minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';

/** The same section shape the job detail uses: a heading on the ground, rows under it. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t">
      <div className="px-gutter pt-2.5 pb-1">
        <div className="dy-eyebrow">{title}</div>
      </div>
      {children}
    </section>
  );
}

/**
 * One schedule: what it files, when it next fires, what became of the last run,
 * and every job it has filed (docs/JOB-LIFECYCLE.md 2.7).
 *
 * The last run's outcome is the fact this page exists for. A schedule that says
 * only "next run 02:00" is the same page whether last night filed a job, skipped
 * because the one before is still open, or failed twice and paused itself; those
 * are three different things to do about it, so the page says which.
 */
export function ScheduleDetail({ scheduleId }: { scheduleId: string }) {
  const detail = useSchedule(scheduleId);
  const { session } = useSession();

  if (detail.isPending) {
    return <p className="text-fg-3 px-gutter py-6 text-sm">Loading…</p>;
  }
  if (detail.error || !detail.data) {
    return (
      <p className="text-bad px-gutter py-6 text-sm">
        {detail.error instanceof Error ? detail.error.message : 'That schedule could not be read.'}
      </p>
    );
  }

  const { schedule, cadence, runs, events } = detail.data;
  // The list's row shape, from the detail's own row, so the verbs below take the
  // same object whichever page they are on.
  const summary: ScheduleSummary = {
    ...schedule,
    cadence,
    title: schedule.template.title,
    repos: [...schedule.template.repos],
    taskCount: schedule.template.tasks.length,
  };

  // The same set that may approve: the person whose approval the row carries,
  // or an admin. The hub enforces it; this decides whether to draw the verbs.
  const mayShape =
    session.isAdmin ||
    schedule.approvedBy.trim().toLowerCase() === session.actor.trim().toLowerCase();

  const rows: { label: string; value: ReactNode }[] = [
    { label: 'Cadence', value: cadence },
    {
      label: 'Next run',
      value: schedule.pausedAt ? (
        <span className="text-warn">Paused, so nothing is scheduled.</span>
      ) : (
        clockTime(schedule.nextRunAt)
      ),
    },
    {
      label: 'Approved by',
      value: (
        <>
          {schedule.approvedBy}
          <span className="text-fg-3"> · every run it files carries this approval</span>
        </>
      ),
    },
    { label: 'Files', value: schedule.template.title },
    { label: 'Goal', value: schedule.template.goal },
  ];
  if (schedule.template.constraints.length) {
    rows.push({
      label: 'Rules',
      value: (
        <ul className="list-disc space-y-0.5 pl-3.5">
          {schedule.template.constraints.map((rule, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: read-only, never reordered.
            <li key={`${index}-${rule}`}>{rule}</li>
          ))}
        </ul>
      ),
    });
  }
  if (schedule.lastRunAt || schedule.lastOutcome) {
    rows.push({
      label: 'Last run',
      value: (
        <>
          {schedule.lastRunAt ? clockTime(schedule.lastRunAt) : 'never filed'}
          {schedule.lastOutcome ? (
            <span
              className={
                schedule.lastOutcome === 'failed'
                  ? 'text-bad'
                  : schedule.lastOutcome === 'skipped'
                    ? 'text-warn'
                    : 'text-ok'
              }
            >
              {' · '}
              {schedule.lastOutcome}
            </span>
          ) : null}
          {schedule.lastError ? (
            <p className="text-fg-3 mt-0.5 break-words">{schedule.lastError}</p>
          ) : null}
        </>
      ),
    });
  }
  if (schedule.pausedAt) {
    rows.push({
      label: 'Why it is paused',
      value: (
        <span className="text-warn">
          {schedule.pauseReason ?? 'No reason was given.'}
          {schedule.pausedBy ? (
            <span className="text-fg-3"> · paused by {schedule.pausedBy}</span>
          ) : null}
        </span>
      ),
    });
  }

  return (
    <div>
      <div className="bg-sunken/60 border-b px-gutter py-2.5">
        <div className="flex items-center gap-2">
          <TaskKey>{shortKey(schedule.id)}</TaskKey>
          <span className="text-fg-3 text-2xs">schedule</span>
          {schedule.pausedAt ? (
            <SchedulePausedChip reason={schedule.pauseReason} className="ml-auto" />
          ) : null}
        </div>
        <h3 className="mt-1 text-md font-semibold">{schedule.name}</h3>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {schedule.template.repos.map((repo) => (
            <RepoChip key={repo} repo={repo} />
          ))}
        </div>
      </div>

      <MetaGrid rows={rows} />

      {schedule.pausedAt && schedule.failuresInARow > 0 ? (
        <section className="bg-gate-bg/50 border-gate-line/60 border-y">
          <div className="px-gutter py-2.5">
            <div className="dy-eyebrow text-gate">Waiting on you</div>
            <p className="text-fg-2 mt-1 text-xs">
              Two runs in a row ended badly, so the hub stopped the clock rather than filing a
              third. Fix what the last run hit, then resume; the count starts again at zero.
            </p>
          </div>
        </section>
      ) : null}

      <Section
        title={`Plan · ${schedule.template.tasks.length} task${schedule.template.tasks.length === 1 ? '' : 's'}`}
      >
        <p className="text-fg-3 px-gutter pb-1.5 text-2xs">
          Approved once, by {schedule.approvedBy}. Every run files these tasks as a job that is
          already through the plan gate; each one still comes back as a pull request to review.
        </p>
        <TemplatePlan tasks={schedule.template.tasks} />
      </Section>

      <Section title={`Runs · ${runs.length} job${runs.length === 1 ? '' : 's'}`}>
        {runs.length ? (
          <ul className="divide-y">
            {runs.map((brief) => (
              <li key={brief.id}>
                <Link
                  to="/jobs/$briefId"
                  params={{ briefId: brief.id }}
                  className="hover:bg-raised/60 flex items-start gap-2 px-gutter py-row transition-colors duration-100"
                >
                  <BriefIcon status={brief.status} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{brief.title}</span>
                    <span className="text-fg-3 text-2xs">{labelForKind(brief.status)}</span>
                  </div>
                  <Age minutes={minutesSince(brief.createdAt)} />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Nothing filed yet. The first run happens at the next firing.</Empty>
        )}
      </Section>

      {/* The same rule the job detail follows for its verbs: the person whose
          approval the row carries, or an admin. The hub enforces it and answers
          with a sentence; this decides whether to draw the buttons, so a member
          reading somebody else's schedule is not offered a Delete that 403s. */}
      {mayShape ? (
        <ScheduleActions schedule={summary} className="border-t" />
      ) : (
        <p className="text-fg-3 border-t px-gutter py-2 text-2xs">
          {schedule.approvedBy} approved this schedule. Running, pausing and deleting it are theirs
          or an admin's.
        </p>
      )}

      <Section title="Events">
        <EventLog events={events.slice(0, 30)} empty="Nothing has happened on this schedule yet." />
      </Section>
    </div>
  );
}
